import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Send, CheckCircle2, Clock, LifeBuoy, MessageSquareHeart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import UnsplashHero from "../components/UnsplashHero";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", company: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.message.trim().length < 10) {
      toast.error("Please add your name and a message (10+ characters)."); return;
    }
    setBusy(true);
    try {
      await api.post("/email/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div data-testid="contact-page">
      <UnsplashHero
        query="postcards letters mailbox travel"
        eyebrow="Contact"
        title="Get in touch"
        subtitle="Questions, feedback or a partnership idea? Send us a message and we'll get back to you."
        testid="contact-hero"
      />
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-14 grid md:grid-cols-[1.4fr_1fr] gap-10">
      <div>
      {sent ? (
        <div className="mt-10 border border-emerald-300 bg-emerald-50/50 p-8 text-center" data-testid="contact-success">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <h2 className="font-heading font-900 text-xl">Message sent</h2>
          <p className="text-muted-foreground mt-1">Thanks for reaching out — we've received your message and will reply to {form.email}.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input aria-hidden tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="hidden" />
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block"><span className="text-sm font-600">Name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary" /></label>
            <label className="block"><span className="text-sm font-600">Email</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary" /></label>
          </div>
          <label className="block"><span className="text-sm font-600">Subject</span>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary" /></label>
          <label className="block"><span className="text-sm font-600">Message</span>
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} required data-testid="contact-message" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary" /></label>
          <button type="submit" disabled={busy} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-600 hover:bg-blue-700 disabled:opacity-60 transition-colors">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send message"}
          </button>
        </form>
      )}
      </div>

      <aside className="space-y-5" data-testid="contact-sidebar">
        <div className="border border-zinc-200 bg-white p-5">
          <div className="flex items-center gap-2 font-heading font-800 text-lg text-zinc-900">
            <Clock className="w-5 h-5 text-primary" /> Response times
          </div>
          <ul className="mt-2 text-sm text-zinc-600 space-y-1.5 list-disc list-inside">
            <li><strong>General questions:</strong> within 2 business days</li>
            <li><strong>Membership &amp; billing:</strong> same business day</li>
            <li><strong>Safety / travel emergency:</strong> use in-app SOS instead — a live guardian responds within minutes</li>
          </ul>
        </div>

        <div className="border border-zinc-200 bg-white p-5">
          <div className="flex items-center gap-2 font-heading font-800 text-lg text-zinc-900">
            <LifeBuoy className="w-5 h-5 text-emerald-600" /> Faster routes
          </div>
          <div className="mt-3 space-y-2 text-sm">
            <Link to="/faq" data-testid="contact-link-faq" className="flex items-center gap-2 text-primary font-600 hover:underline">
              <Sparkles className="w-3.5 h-3.5" /> Read the FAQ first
            </Link>
            <Link to="/feedback" data-testid="contact-link-feedback" className="flex items-center gap-2 text-primary font-600 hover:underline">
              <MessageSquareHeart className="w-3.5 h-3.5" /> Share product feedback
            </Link>
            <a href="mailto:hello@travelerpulse.pro" data-testid="contact-link-email" className="flex items-center gap-2 text-primary font-600 hover:underline">
              <Mail className="w-3.5 h-3.5" /> hello@travelerpulse.pro
            </a>
          </div>
        </div>

        <div className="border border-zinc-200 bg-zinc-50 p-5 text-xs text-zinc-600 leading-relaxed">
          TravelerPulse.pro™ is part of the SocialPulse Network.
          For press, partnerships or advertising, please mention the topic in the
          subject line so it reaches the right team.
        </div>
      </aside>
      </div>
    </div>
  );
}
